import { useRef, useState } from "react";

import { crmPostventaIpcApi } from "../crmPostventaIpcApi";
import { createCorrelationId, describeSaveError, isConflict } from "./gestionarIpcModel";

/**
 * Responsible commands of one gestión (D25), shared by Gestionar and the Detalle: Tomar, Liberar and Asignar… with the
 * current versionToken. Tomar and Liberar use one correlationId per attempt (operation and version), so "Reintentar"
 * after a network error repeats the same request and gets the server's idempotent answer (RN5); the assign dialog
 * brings its own correlationId.
 *
 * `saving` / `setSaving` is the caller's busy flag: one command at a time on the screen. A stale gestión (version
 * conflict, see isConflict) goes to onConflict(error); onSuccess(title, text) runs after each successful command (text
 * undefined lets the caller pick its default). onStart() runs before Tomar / Liberar. Their other errors stay in
 * `error` ({ title, text, retry, operation }) until clearError() or the next command; `retry()` repeats that operation.
 */
export default function useOwnershipCommands({ gestionId, versionToken, comercio, saving, setSaving, mountedRef, onStart, onSuccess, onConflict }) {
  const [error, setError] = useState(null);
  const attemptRef = useRef(null);

  const correlationFor = (operation) => {
    const key = JSON.stringify({ operation, versionToken });
    if (attemptRef.current?.key !== key) attemptRef.current = { key, correlationId: createCorrelationId() };
    return attemptRef.current.correlationId;
  };

  const run = async (operation, request, title, text) => {
    if (saving) return;
    const correlationId = correlationFor(operation);
    setSaving(true);
    setError(null);
    onStart?.();
    try {
      await request(correlationId);
      if (!mountedRef.current) return;
      attemptRef.current = null;
      await onSuccess(title, text);
    } catch (requestError) {
      if (!mountedRef.current) return;
      if (isConflict(requestError)) onConflict(requestError);
      else setError({ ...describeSaveError(requestError), operation });
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const tomar = () =>
    run("TOMAR", (correlationId) => crmPostventaIpcApi.tomar(gestionId, { versionToken, correlationId }), "Tomaste la gestión");

  const liberar = () =>
    run(
      "LIBERAR",
      (correlationId) => crmPostventaIpcApi.liberar(gestionId, { versionToken, correlationId }),
      "Liberaste la gestión",
      `${comercio} · queda Sin asignar`
    );

  const retry = () => (error?.operation === "LIBERAR" ? liberar() : tomar());

  // Submit of AsignarGestionDialog: a stale gestión goes to onConflict; any other error goes back to the dialog.
  const asignar = async ({ usuarioId, observacion, correlationId, nombre, reasignada }) => {
    try {
      await crmPostventaIpcApi.asignar(gestionId, { usuarioId, observacion, versionToken, correlationId });
    } catch (assignError) {
      if (!isConflict(assignError)) throw assignError;
      if (mountedRef.current) onConflict(assignError);
      return;
    }
    if (!mountedRef.current) return;
    await onSuccess(reasignada ? `Gestión reasignada a ${nombre}` : `Gestión asignada a ${nombre}`);
  };

  return {
    error,
    clearError: () => setError(null),
    tomar,
    liberar,
    retry,
    asignar,
  };
}
